<template>
  <BravadoInfiniteScrollBase
    :loading="loading"
    :container="container"
    :scroll-handler="handleSyncPage"
    @on-scroll-top="handlePrevious"
    @on-scroll-bottom="handleNext"
    @on-container-element-ready="onContainerElementReady"
  >
    <BravadoInfiniteScrollLoader
      v-if="showTopLoader"
      slot="topLoader"
      class="bravado-infinite-scroll__loader"
      :with-links="withLinks"
    />
    <BravadoInfiniteScrollLoader
      v-if="showBottomLoader"
      slot="bottomLoader"
      class="bravado-infinite-scroll__loader bravado-infinite-scroll__loader--bottom"
      :links="numberedPagination"
      :dot-count="loaderDotCount"
      :with-links="withLinks"
    />
    <slot :get-scroll-meta="getScrollMeta" />
  </BravadoInfiniteScrollBase>
</template>

<script lang="ts">
import type { PropOptions } from 'vue';
import Vue from 'vue';
import BravadoInfiniteScrollLoader from './BravadoInfiniteScrollLoader.vue';
import { getCurrentPage, scrollToPage, scrollIntoView, getElementByPostSlug } from './utils';
import BravadoInfiniteScrollBase from '@/components/common/BravadoInfiniteScroll/BravadoInfiniteScrollBase.vue';
import { range } from '@/components/utils/core/array';
import { feedTabKeys } from '~/components/partials/war-room/constants';

const LOADER_DOT_COUNT = 5;

interface IData {
  loading: boolean;
  direction?: 'up' | 'down';
  loaderDotCount: number;
  loadedPages: number[];
  containerElement: HTMLElement | null;
}

interface IPagination {
  nextPage: number | null;
  previousPage: number | null;
  totalPages: number | null;
  loading: boolean;
  loadedPages: Array<number>;
}

interface IScrollTo {
  slug: string | null;
  offsetTop: number | null;
}

export { BravadoInfiniteScrollLoader };

export default Vue.extend({
  name: 'BravadoInfiniteScrollOffset',
  components: {
    BravadoInfiniteScrollLoader,
    BravadoInfiniteScrollBase,
  },
  props: {
    pagination: {
      type: Object,
      required: true,
    } as PropOptions<IPagination>,
    page: {
      type: Number,
      required: true,
    } as PropOptions<number>,
    container: {
      type: String,
      default: '',
    } as PropOptions<string>,
    withLinks: {
      type: Boolean,
      default: true,
    },
    scrollTo: {
      type: Object,
      default: () => ({
        slug: null,
        offsetTop: null,
      }),
    } as PropOptions<IScrollTo>,
  },
  data(): IData {
    return {
      loading: false,
      direction: undefined,
      loaderDotCount: LOADER_DOT_COUNT,
      loadedPages: [this.page],
      containerElement: null,
    };
  },
  computed: {
    showTopLoader(): boolean {
      return this.pagination.loading && this.direction === 'up';
    },
    showBottomLoader(): boolean {
      return this.page !== this.pagination.totalPages;
    },
    numberedPagination(): Array<string> {
      const startFrom = Math.max(1, this.page - Math.floor(this.loaderDotCount / 2));
      const countTill = Math.min(this.pagination.totalPages || 0, startFrom + this.loaderDotCount);

      const pageNumbers = range(startFrom, countTill).filter(el => el !== this.page);

      return pageNumbers.map(n => {
        const query: { page: string; sort_type?: string; tab?: string } = { page: String(n) };

        if (
          typeof this.$route.query.tab === 'string' &&
          Object.values(feedTabKeys).includes(this.$route.query.tab as (typeof feedTabKeys)[keyof typeof feedTabKeys])
        ) {
          query.tab = this.$route.query.tab;
        }

        if (this.$route.query.sort_type) {
          query.sort_type = String(this.$route.query.sort_type);
        }

        const { href } = this.$router.resolve({
          name: this.$route.name!,
          params: this.$route.params,
          query,
        });

        return href;
      });
    },
  },
  methods: {
    onContainerElementReady(element: HTMLElement) {
      this.containerElement = element;

      const page = Number(this.$route.query.page);
      const scroll = this.$el as HTMLElement;

      if (scroll && this.scrollTo.slug) {
        scrollIntoView(scroll, this.scrollTo.slug, this.scrollTo.offsetTop as number);
      } else if (page && page !== 1) {
        scroll.scrollIntoView({ block: 'center' });
      }

      this.$emit('scroll:init');
    },
    handleSyncPage() {
      const page = getCurrentPage(this.$el as HTMLElement, this.containerElement);

      if (page && page !== this.$route.query.page) {
        this.$router.replace({ query: { ...this.$route.query, page } });
      }
    },
    async handleLoad(page: number): Promise<void> {
      this.loading = true;
      // eslint-disable-next-line
      const load = (this.$listeners as any).load;
      await load(page, this.direction);
      this.loading = false;
    },
    async handlePrevious(): Promise<void> {
      const newPage = this.page <= 2 ? 1 : this.page - 1;

      if (this.loadedPages.includes(newPage)) return;
      this.loadedPages.push(newPage);

      this.direction = 'up';
      await this.handleLoad(newPage);
      this.$nextTick(() => {
        scrollToPage(this.$el as HTMLElement, newPage + 1);
      });
    },
    async handleNext() {
      const newPage = this.page + 1;
      if (newPage > (this.pagination.totalPages || 0)) return;
      if (this.loadedPages.includes(newPage)) return;
      this.loadedPages.push(newPage);

      this.direction = 'down';
      await this.handleLoad(newPage);
    },
    getScrollMeta(slug: string): { slug?: string; offsetTop?: number } {
      const element = getElementByPostSlug(this.$el as HTMLElement, slug);
      return element
        ? {
            slug,
            offsetTop: element.getBoundingClientRect().top,
          }
        : {};
    },
  },
});
</script>

<style scoped lang="scss">
.bravado-infinite-scroll {
  &__loader {
    margin: 0 0 12px;

    &--bottom {
      margin: 12px 0 0;
    }
  }
}
</style>
